import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import styled from '@emotion/styled';
import * as Yup from 'yup';
import { useTheme } from 'emotion-theming';
import { push } from 'connected-react-router';

import Button from '../../../../shared/_TBComponents/Button';
import Checkmark from '../../../../shared/_TBComponents/Checkmark';
import Link from '../../../../shared/_TBComponents/Link';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import SubText from '../../../../shared/_TBComponents/SubText';
import Text from '../../../../shared/_TBComponents/Text';
import { clearEasyJoinError, submitEasyJoinCode } from '../../ducks/registrationActions';

const EasyJoinFieldContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  width: 100%;

  input {
    text-transform: uppercase;
  }
`;

const EasyJoinSeparator = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-left: 10px;
  padding-right: 10px;
`;

const ErrorAndName = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 8px;
  width: 100%;
`;

const FieldError = styled('div')`
  color: #ff6600;
  font-size: 14px;
`;

const FormGroup = styled('div')`
  z-index: 1;
  
  .button {
    margin-bottom: 20px;
  }
  
  .button-sign-in {
    margin-bottom: 10px;
  }
  
  .divider-line {
    border-top: 2px solid rgb(232,232,232);
    display: flex;
    margin-bottom: 20px;
    margin-left: 10px;
    margin-right: 10px;
    margin-top: 20px;
    width: 100%;
  }
  
  .easy-join {
    margin-bottom: 15px;
    margin-top: 30px;
  }
  
  .return-link {
    margin-top: 30px;
  }
  
  label {
    width: 100%;
  }
  
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

export const InteriorDiv = styled('div')`
  display: flex;
  flex-direction: column;
  height: 336px;
`;

export const SecondButtonWrapper = styled('div')`
  margin-top: 15px;
`;

export const SpinnerContainer = styled('div')`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  z-index: 0;
`;

export const TopText = styled('div')`
  display: flex;
  height: 40px;
  justify-content: center;
  margin-bottom: 20px;
  text-align: center;
  width: 100%;
`;

const EasyJoinSchema = Yup.object().shape({
  easyJoin: Yup.string().required('Enter Code').min(4, ''),
  easyJoin2: Yup.string().required('Enter Code').min(4, ''),
  password: Yup.string()
    .required('Please enter a password'),
});

const focusChange = (e, secondFieldRef) => {
  if (e.target.value.length >= e.target.getAttribute('maxlength')) {
    secondFieldRef.current.focus();
  }
};

const EasyJoinForm = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const easyJoinError = useSelector((state) => state.registration.data.easyJoinError);
  const isEasyJoinCompleted = useSelector((state) => state.registration.data.isEasyJoinCompleted);
  const isEasyJoinLoading = useSelector((state) => state.registration.ui.isEasyJoinLoading);

  const [completeAnimation, setCompleteAnimation] = useState(false);

  const secondFieldRef = useRef();
  const thirdFieldRef = useRef();

  const handleClearEasyJoinError = () => {
    dispatch(clearEasyJoinError());
  };

  useEffect(() => {
    if (isEasyJoinCompleted) {
      setCompleteAnimation(true);
      setTimeout(() => {
        dispatch(push('/signup/easy-join-confirmation'));
      }, 1500);
    }
  }, [isEasyJoinCompleted]);

  return (
    <Formik
      initialValues={{
        easyJoin: '',
        easyJoin2: '',
        password: '',
      }}
      onSubmit={(values) => {
        const submissionOb = {
          easyJoinPassword: values.password,
          easyJoinCode: values.easyJoin + values.easyJoin2,
        };
        dispatch(submitEasyJoinCode(submissionOb));
      }}
      validationSchema={EasyJoinSchema}
      render={({
        errors,
        touched,
        handleSubmit,
      }) => {
        // Render loading, success, or form
        if (isEasyJoinLoading || isEasyJoinCompleted) {
          return (
            <InteriorDiv>
              {!completeAnimation ? (
                <SpinnerContainer>
                  <Spinner />
                </SpinnerContainer>
              ) : (
                <SpinnerContainer>
                  <Checkmark />
                </SpinnerContainer>
              )}
            </InteriorDiv>
          );
        }

        return (
          <InteriorDiv>
            <TopText>
              <SubText>
                Enter Easy-Join Information provided by your coach/trainer below
              </SubText>
            </TopText>
            <Form>
              <FormGroup className='form-group'>
                <label htmlFor='easy-join-field'>
                  <ErrorAndName>
                    <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
                      Easy Join Code
                    </Text>
                    {(errors.easyJoin || errors.easyJoin2)
                      && (touched.easyJoin || touched.easyJoin2) && (
                        <FieldError className='text-center'>
                          {errors.easyJoin || errors.easyJoin2}
                        </FieldError>
                    )}
                  </ErrorAndName>
                  <EasyJoinFieldContainer>
                    <Field
                      className='form-control'
                      id='easy-join-field'
                      maxLength='4'
                      name='easyJoin'
                      onInput={(e) => focusChange(e, secondFieldRef)}
                      placeholder='XXXX'
                      type='text'
                    />
                    <EasyJoinSeparator>-</EasyJoinSeparator>
                    <Field
                      className='form-control'
                      id='easy-join-field2'
                      innerRef={secondFieldRef}
                      maxLength='4'
                      name='easyJoin2'
                      onInput={(e) => focusChange(e, thirdFieldRef)}
                      placeholder='XXXX'
                      type='text'
                    />
                  </EasyJoinFieldContainer>
                </label>
              </FormGroup>
              <FormGroup className='form-group'>
                <label htmlFor='password-field'>
                  <ErrorAndName>
                    <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>Password</Text>
                    {errors.password && touched.password && (
                      <FieldError className='text-center'>
                        {errors.password}
                      </FieldError>
                    )}
                    {easyJoinError && !errors.password && (
                      <FieldError className='text-center'>
                        Code or password is incorrect
                      </FieldError>
                    )}
                  </ErrorAndName>
                  <Field
                    autoComplete='off'
                    className='form-control'
                    id='password-field'
                    innerRef={thirdFieldRef}
                    name='password'
                    onFocus={() => handleClearEasyJoinError()}
                    placeholder='Password'
                    type='password'
                  />
                </label>
              </FormGroup>
              <FormGroup className='text-center'>
                <Button
                  bgColor={theme.colors.orange}
                  disabled={
                    errors.easyJoin || errors.easyJoin2 || errors.password
                  }
                  fontSize='14px'
                  fullWidth
                  id='submit'
                  large
                  name='submit'
                  noBorder
                  onClick={handleSubmit}
                  rounded
                  txtColor={theme.colors.white}
                  type='submit'
                  upperCase
                />
                <Link
                  allCaps
                  className='return-link'
                  color='lightText.1'
                  href='/login'
                >
                  Return to Login
                </Link>
              </FormGroup>
            </Form>
          </InteriorDiv>
        );
      }}
    />
  );
};

export default EasyJoinForm;
